// ErrorBoundary
export { default as ErrorBoundary } from './ErrorBoundary';
export { default as AppErrorBoundary } from './AppErrorBoundary';
// General Utilities
export {
	randomId,
	centsToDollars,
	userDisplayNameParser,
	notifyToast,
	dateRangeReportParser,
	emailIsValid,
	getOrdinal,
} from './general';

// PropTypes
export { apolloSharedPropTypes } from './PropTypes/apolloSharedPropTypes';
export { reactRouterPropTypes } from './PropTypes/reactRouterPropTypes';

// CP3 Styles Helpers
export {
	Sizes,
	Colors,
	HoverColors,
	media,
	minMedia,
	modifiedMinWidth,
	modifiedMaxWidth,
	maxMedia,
	BaseStyles,
	SelectTitle,
	Spacer,
	StyledLink,
	PUSHER_AND_SIDE_MS_TRANSITION,
	addColorOpacity,
	modColorOpacity,
} from './cp3/styles';

// Currency
export { NumberFormatter } from './cp3/NumberFormatter';
export { dateFormatter } from './cp3/dateFormatter';
export {
	isPathNameAllowed,
	parseLocationStartEndTime,
	parseDateTime,
	getShortDateFormat,
} from './cp3/general';

// Server Errors Object
export { ServerErrors } from './errors';

// LocalStorage Helper
export { lStorage } from './LocalStorage/LocalStorageHelper';

// Components Helper
export {
	default as ConfirmationModal,
} from './cp3/components/ConfirmationModal';

export { TitleSection } from './cp3/components/TitleSection';
export { Image } from './cp3/components/Image';
export { LoadingScreen } from './cp3/components/LoadingScreen';
export { default as SubSection } from './cp3/components/SubSection';
export {
	default as ExpandAndCollapse,
} from './cp3/components/ExpandAndCollapse';
export { Label } from './cp3/components/Label';
export { DynamicLink } from './cp3/components/DynamicLink';
export {
	default as RelativeVisibility,
} from './cp3/components/RelativeVisibility';
export { default as TooltipStyled } from './cp3/components/TooltipStyled';
export { default as TooltipedText } from './cp3/components/TooltipedText';
export {
	default as AppProvider,
	AppConsumer,
	withAppContext,
	AppContext,
} from './cp3/context/AppContext';
export {
	default as ErrorBoundaryProvider,
	withErrorBoundaryContext,
} from './cp3/context/ErrorBoundaryContext';
export {
	SNACKBAR_TYPES,
	SnackbarsProvider,
	SnackbarsConsumer,
	withSnackbarsContext,
	useSnackbar,
} from './cp3/context/SnackbarsContext';
export { default as withContext } from './cp3/hoc/withContext';
export { default as contextQuery } from './cp3/hoc/contextQuery';
export { default as withIntlProvider } from './cp3/hoc/withIntlProvider';
export { INTERVAL_TIME, default as IdleTimer } from './cp3/IdleTimer';
export {
	FormikTextField,
	FormikTextArea,
	FormikRadioGroup,
	FormikStellarTextField,
	FormikAutoComplete,
	FormikCheckbox,
	FormikToggleButtonsGroup,
	FormikTabs,
	formikConnector,
	FormikTimePicker,
} from './cp3/components/FormikInputs';
export {
	PrintableAccordion,
	PrintableAccordionHeader,
} from './cp3/components/PrintableAccordion';
export { CircularProgress as default } from './cp3/components/CircularProgress';
export { CircularProgress } from './cp3/components/CircularProgress';
export { default as Snackbar } from './cp3/components/Snackbar';
export { default as SettingsModal } from './cp3/components/SettingsModal';

export {
	getLanguageCode,
	localizeFormOptions,
	localizedDistanceStrict,
} from './cp3/localization';

// CookieStorage Helper
export { cookiesHelper } from './Cookies/CookiesHelper';

// Constants
export {
	APOLLO_LOADING,
	APOLLO_SET_VARIABLES,
	APOLLO_FETCH_MORE,
	APOLLO_REFETCH,
	APOLLO_POLL,
	APOLLO_READY,
	APOLLO_ERROR,
} from './constants/apolloNetworkStatus';

export {
	EMAIL_REGEX,
	PASSWORD_REGEX,
	NOT_EMPTY_REGEX,
	ALPHANUMERIC_REGEX,
	NUMERIC_REGEX,
	TEXT_ONLY_REGEX,
	PHONE_NUMBER_REGEX,
	ALPHANUMERIC_WITH_WHITE_SPACES_REGEX,
	escapeRegExp,
	RATE_REGEX,
	getRateRegex,
	NUMERIC_WITH_DECIMAL_REGEX,
	NUMERIC_WITH_COMMA_DECIMAL_REGEX,
	TWELVE_HOURS_REGEX,
	IP_REGEX,
	FIRST_LAST_NAME_REGEX,
	COLOR_REGEX,
	HTTP_REGEX,
	SHORT_DATE_FORMAT_REGEX,
	GRATER_THAN_ZERO_WITH_TWO_DECIMAL_PLACES_REGEX,
	default as regexPatterns,
} from './constants/regex';

export {
	BE_DATE_FORMAT,
	BE_DATE_FORMAT_FNS,
	INVALID_DB_DATE,
	DATE_FORMAT,
	TIME_WITH_PERIOD_FORMAT,
	TIME_WITH_SECONDS_FORMAT,
	TIME_WITH_PERIOD_FORMAT_FNS,
	TIME_WITH_MINUTES_FORMAT_FNS,
	TIME_MINUTES_SECONDS_A_FORMAT_FNS,
	TIME_WITH_MINUTES_SECONDS_FORMAT_FNS,
	TIME_24HOUR_WITH_SECONDS_FORMAT,
	TIME_24HOUR_WITH_MINUTES_FORMAT,
	DATE_FORMAT_FNS,
	DISPLAY_DATE_FORMAT,
	SIMPLE_TIME_WITH_PERIOD_FORMAT_FNS,
	BE_DATE_TIME_FORMAT_FNS,
	DEFAULT_SHORT_DATE_FORMAT_FNS,
	INVENTORY_DATE_FORMAT,
} from './constants/datetime';

export {
	DAY_START_END_SETTING,
	MIDNIGHT_SETTING,
} from './constants/dayRollover';

export { CARD_PAY_TYPE, CASH_PAY_TYPE } from './constants/payments';

export { LAVU_ADMIN_USERNAME } from './constants/lavuAdmin';

export {
	ADMIN_ACCESS_LEVEL,
	OWNER_ACCESS_LEVEL,
	hasPathAccess,
} from './constants/accessLevels';

export {
	ASCENDING,
	DESCENDING,
	SORT_DIRECTIONS,
} from './constants/sortDirections';

// SVG React Components
export { IconInfoPrimary, IconError, Exclamation } from './cp3/images';

// Queries
export { allLocations } from './queries';

export const PAGE_SIZE = 25;

export { intlShape } from './PropTypes/reactIntlPropTypes';
